/**
 * API Client for Luxury Real Estate Backend
 * Connects React frontend to PHP backend
 */

// Auto-detect API URL: use VITE_API_URL if set, otherwise same-origin /api
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// =====================================================
// AUTH TOKEN MANAGEMENT
// =====================================================
let authToken: string | null = localStorage.getItem('admin_token');

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    localStorage.setItem('admin_token', token);
  } else {
    localStorage.removeItem('admin_token');
  }
};

export const getAuthToken = () => authToken;

export const isAuthenticated = () => !!authToken;

// =====================================================
// API REQUEST HELPER
// =====================================================
interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string>;
  pagination?: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_more: boolean;
  };
}

async function parseApiJson<T>(response: Response): Promise<ApiResponse<T>> {
  const rawResponse = await response.text();

  if (!rawResponse.trim()) {
    if (response.ok) {
      return { success: true, message: 'Success' };
    }

    return {
      success: false,
      message: `Server error (status ${response.status}). Empty response from API.`,
    };
  }

  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    console.error('API returned non-JSON response:', contentType, rawResponse.substring(0, 300));
    return {
      success: false,
      message: `Server error (status ${response.status}). The API returned an unexpected response.`,
    };
  }

  try {
    return JSON.parse(rawResponse) as ApiResponse<T>;
  } catch (error) {
    console.error('API returned invalid JSON:', rawResponse.substring(0, 300), error);
    return {
      success: false,
      message: `Server error (status ${response.status}). Invalid JSON response from API.`,
    };
  }
}


async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  if (authToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized
    // IMPORTANT: Don't hard-redirect on login failures, otherwise the toast flashes and disappears.
    // Only redirect when we *were* authenticated (token existed) and the request wasn't the login call.
    if (response.status === 401) {
      const isLoginRequest = endpoint === '/auth/login';
      const hadToken = !!authToken;

      setAuthToken(null);

      if (hadToken && !isLoginRequest) {
        window.location.href = '/admin/login';
      }
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true, message: 'Success' };
    }

    return parseApiJson<T>(response);
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error',
    };
  }
}

// =====================================================
// AUTH API
// =====================================================
export interface AdminUser {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
}

export interface LoginResponse {
  token: string;
  user: AdminUser;
}

export const authApi = {
  login: async (username: string, password: string) => {
    const response = await apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.success && response.data?.token) {
      setAuthToken(response.data.token);
    }
    
    return response;
  },
  
  logout: async () => {
    await apiRequest('/auth/logout', { method: 'POST' });
    setAuthToken(null);
  },
  
  getMe: () => apiRequest<AdminUser>('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    }),
};

// =====================================================
// LEADS API
// =====================================================
export interface Lead {
  id: number;
  name: string;
  email: string;
  phone?: string;
  message?: string;
  property_interest?: string;
  budget_range?: string;
  source: 'contact_form' | 'popup' | 'property_inquiry' | 'newsletter';
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'closed';
  notes?: string;
  assigned_to?: number;
  created_at: string;
  updated_at: string;
}

export interface LeadsListParams {
  page?: number;
  per_page?: number;
  status?: string;
  source?: string;
  search?: string;
}

export const leadsApi = {
  list: (params?: LeadsListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.search) searchParams.set('search', params.search);
    
    return apiRequest<Lead[]>(`/leads?${searchParams}`);
  },
  
  get: (id: number) => apiRequest<Lead>(`/leads/${id}`),
  
  create: (data: Partial<Lead>) =>
    apiRequest<Lead>('/leads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Lead>) =>
    apiRequest<Lead>(`/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/leads/${id}`, { method: 'DELETE' }),
  
  exportCsv: () => `${API_BASE_URL}/leads/export`,
};

// =====================================================
// PROPERTIES API
// =====================================================
export interface PropertyImage {
  id: number;
  property_id: number;
  image_url: string;
  image_type: 'uploaded' | 'external_url';
  alt_text?: string;
  is_primary: boolean;
  sort_order: number;
}

export interface Property {
  id: number;
  title: string;
  slug: string;
  description?: string;
  short_description?: string;
  price: number;
  price_label?: string;
  property_type: 'villa' | 'apartment' | 'penthouse' | 'mansion' | 'estate' | 'townhouse';
  status: 'available' | 'sold' | 'pending' | 'off_market' | 'under_construction' | 'ready_to_move' | 'newly_launched' | 'pre_launch';
  featured: boolean;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  bedrooms?: number;
  bathrooms?: number;
  area_sqft?: number;
  lot_size_sqft?: number;
  year_built?: number;
  parking_spaces?: number;
  amenities?: string[];
  highlights?: string[];
  gallery_images?: string[];
  map_address?: string;
  meta_title?: string;
  meta_description?: string;
  is_active: boolean;
  view_count: number;
  images?: PropertyImage[];
  primary_image?: string;
  created_at: string;
  updated_at: string;
}

export interface PropertiesListParams {
  page?: number;
  per_page?: number;
  featured?: boolean;
  status?: string;
  type?: string;
  city?: string;
  min_price?: number;
  max_price?: number;
  bedrooms?: number;
  search?: string;
  admin?: boolean;
}

export const propertiesApi = {
  list: (params?: PropertiesListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    if (params?.featured) searchParams.set('featured', 'true');
    if (params?.status) searchParams.set('status', params.status);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.city) searchParams.set('city', params.city);
    if (params?.min_price) searchParams.set('min_price', String(params.min_price));
    if (params?.max_price) searchParams.set('max_price', String(params.max_price));
    if (params?.bedrooms) searchParams.set('bedrooms', String(params.bedrooms));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.admin) searchParams.set('admin', 'true');
    
    return apiRequest<Property[]>(`/properties?${searchParams}`);
  },
  
  get: (id: number) => apiRequest<Property>(`/properties/${id}`),
  
  getBySlug: (slug: string) => apiRequest<Property>(`/properties/slug/${slug}`),
  
  create: (data: Partial<Property>) =>
    apiRequest<Property>('/properties', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Property>) =>
    apiRequest<Property>(`/properties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/properties/${id}`, { method: 'DELETE' }),
  
  addImage: (propertyId: number, imageUrl: string, isPrimary = false, altText = '') =>
    apiRequest<PropertyImage>(`/properties/${propertyId}/images`, {
      method: 'POST',
      body: JSON.stringify({
        image_url: imageUrl,
        is_primary: isPrimary,
        alt_text: altText,
      }),
    }),
  
  deleteImage: (propertyId: number, imageId: number) =>
    apiRequest(`/properties/${propertyId}/images/${imageId}`, { method: 'DELETE' }),
};

// =====================================================
// TESTIMONIALS API
// =====================================================
export interface Testimonial {
  id: number;
  client_name: string;
  client_title?: string;
  client_image?: string;
  testimonial_text: string;
  rating: number;
  property_id?: number;
  property_title?: string;
  is_featured: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const testimonialsApi = {
  list: (featured?: boolean, all?: boolean) => {
    const params = new URLSearchParams();
    if (featured) params.set('featured', 'true');
    if (all) params.set('all', 'true');
    return apiRequest<Testimonial[]>(`/testimonials?${params}`);
  },
  
  get: (id: number) => apiRequest<Testimonial>(`/testimonials/${id}`),
  
  create: (data: Partial<Testimonial>) =>
    apiRequest<Testimonial>('/testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Testimonial>) =>
    apiRequest<Testimonial>(`/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/testimonials/${id}`, { method: 'DELETE' }),
};

// =====================================================
// PARTNERS API
// =====================================================
export interface Partner {
  id: number;
  name: string;
  logo_url: string;
  website_url?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export const partnersApi = {
  list: (all?: boolean) => {
    const params = all ? '?all=true' : '';
    return apiRequest<Partner[]>(`/partners${params}`);
  },
  
  create: (data: Partial<Partner>) =>
    apiRequest<Partner>('/partners', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Partner>) =>
    apiRequest<Partner>(`/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/partners/${id}`, { method: 'DELETE' }),
  
  reorder: (order: number[]) =>
    apiRequest('/partners/reorder', {
      method: 'PUT',
      body: JSON.stringify({ order }),
    }),
};

// =====================================================
// SETTINGS API
// =====================================================
export interface SiteSettings {
  general: Record<string, { value: string | boolean; type: string }>;
  contact: Record<string, { value: string | boolean; type: string }>;
  seo: Record<string, { value: string | boolean; type: string }>;
  homepage: Record<string, { value: string | boolean; type: string }>;
  popup: Record<string, { value: string | boolean; type: string }>;
}

export const settingsApi = {
  getAll: () => apiRequest<SiteSettings>('/settings'),
  
  get: (key: string) => apiRequest<{ key: string; value: unknown }>(`/settings/${key}`),
  
  update: (settings: Record<string, unknown>) =>
    apiRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  
  updateOne: (key: string, value: unknown) =>
    apiRequest(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),
};

// =====================================================
// POPUP API
// =====================================================
export interface PopupSettings {
  popup_type: string;
  is_enabled: boolean;
  title: string;
  description: string;
  button_text: string;
  delay_seconds: number;
  show_on_exit_intent: boolean;
  background_image?: string;
}

export const popupApi = {
  get: (type = 'lead_magnet') => apiRequest<PopupSettings>(`/popup/${type}`),
  
  update: (type: string, data: Partial<PopupSettings>) =>
    apiRequest<PopupSettings>(`/popup/${type}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// =====================================================
// DASHBOARD API
// =====================================================
export interface DashboardStats {
  leads: {
    total: number;
    new: number;
    this_month: number;
    by_status: Array<{ status: string; count: number }>;
    by_source: Array<{ source: string; count: number }>;
  };
  properties: {
    total: number;
    active: number;
    available: number;
    sold: number;
    pending: number;
    featured: number;
    total_views: number;
    by_type: Array<{ property_type: string; count: number }>;
  };
  testimonials: {
    total: number;
    active: number;
    featured: number;
  };
  partners: {
    total: number;
    active: number;
  };
  recent_leads: Lead[];
  top_properties: Property[];
}

export interface ActivityLog {
  id: number;
  admin_id: number;
  username: string;
  full_name: string;
  action_type: string;
  entity_type: string;
  entity_id: number;
  description: string;
  ip_address: string;
  created_at: string;
}

export const dashboardApi = {
  getStats: () => apiRequest<DashboardStats>('/dashboard/stats'),
  
  getActivity: (limit = 20) =>
    apiRequest<ActivityLog[]>(`/dashboard/activity?limit=${limit}`),
};

// =====================================================
// UPLOAD API
// =====================================================
export const uploadApi = {
  uploadImage: async (file: File, folder = 'general'): Promise<ApiResponse<{ url: string; filename: string }>> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);
    
    const response = await fetch(`${API_BASE_URL}/upload/image`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });
    
    return parseApiJson<{ url: string; filename: string }>(response);
  },
  
  uploadImages: async (files: File[], folder = 'general'): Promise<ApiResponse<Array<{ url: string; filename: string }>>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images[]', file));
    formData.append('folder', folder);
    
    const response = await fetch(`${API_BASE_URL}/upload/images`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    });
    
    return parseApiJson<Array<{ url: string; filename: string }>>(response);
  },
};

// =====================================================
// THEME API (Updated for Visual Editor)
// =====================================================
export interface ThemeData {
  id?: number;
  preset_id: string;
  name: string;
  description?: string;
  category?: string;
  business_type?: string;
  animation_style?: string;
  colors: Record<string, string>;
  typography: Record<string, string>;
  spacing: Record<string, string>;
  sections: Array<{ id: string; name: string; visible: boolean; order: number }>;
  page_content?: Record<string, Record<string, unknown>>;
  custom_sections?: Record<string, Array<{ id: string; name: string; visible: boolean; order: number; type?: string; blockType?: string; pageId?: string }>>;
  is_active?: boolean;
  is_draft?: boolean;
}

export const themeApi = {
  list: () => apiRequest<ThemeData[]>('/theme'),

  getActive: () => apiRequest<ThemeData>('/theme/active'),

  save: (data: ThemeData & { is_draft?: boolean; is_active?: boolean }) =>
    apiRequest<{ id: number }>('/theme', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    apiRequest(`/theme/${id}`, { method: 'DELETE' }),
};

// =====================================================
// INTEGRATIONS API
// =====================================================
export const integrationsApi = {
  get: () => apiRequest<Record<string, { enabled: boolean; trackingId: string; extraConfig?: unknown }>>('/integrations'),

  getPublic: () => apiRequest<Record<string, { enabled: boolean; trackingId: string; extraConfig?: unknown }>>('/integrations/public'),

  update: (data: Record<string, { enabled: boolean; trackingId?: string; verificationCode?: string; code?: string }>) =>
    apiRequest('/integrations', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// =====================================================
// CONTENT API (Generic CMS for page-specific data)
// =====================================================
export const contentApi = {
  getPage: (pageSlug: string) =>
    apiRequest<Record<string, { type: string; value: unknown }>>(`/content/${pageSlug}`),

  getSection: (pageSlug: string, key: string) =>
    apiRequest<{ page: string; section: string; type: string; value: unknown }>(`/content/${pageSlug}/${key}`),

  updateSection: (pageSlug: string, key: string, content: unknown, type = 'json') =>
    apiRequest(`/content/${pageSlug}/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ content: typeof content === 'string' ? content : JSON.stringify(content), type }),
    }),
};

// =====================================================
// USERS CRUD API
// =====================================================
export const usersApiCrud = {
  list: () =>
    apiRequest<Array<{
      id: number; username: string; email: string; full_name: string;
      role: string; permissions: string[]; is_active: boolean;
      created_at: string; last_login?: string;
    }>>('/users'),

  create: (data: Record<string, unknown>) =>
    apiRequest('/users', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: number | string, data: Record<string, unknown>) =>
    apiRequest(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: number | string) =>
    apiRequest(`/users/${id}`, { method: 'DELETE' }),
};

// =====================================================
// FRONTEND STATS API (Hero section stats)
// =====================================================
export const frontendStatsApi = {
  getAll: () =>
    apiRequest<Array<{ stat_key: string; stat_value: string; stat_label: string }>>('/stats'),

  update: (data: Record<string, { value: string; label: string }>) =>
    apiRequest('/stats', { method: 'PUT', body: JSON.stringify(data) }),
};

// =====================================================
// HEADER FOOTER API
// =====================================================
export const headerFooterApi = {
  get: () =>
    apiRequest<{ header: Record<string, unknown>; footer: Record<string, unknown> }>('/header-footer'),

  update: (data: { header?: Record<string, unknown>; footer?: Record<string, unknown> }) =>
    apiRequest('/header-footer', { method: 'PUT', body: JSON.stringify(data) }),
};

// =====================================================
// PAGE SEO API (dedicated page_seo table)
// =====================================================
export const pageSeoApi = {
  getAll: () => apiRequest<Array<{ page_slug: string; page_name: string; meta_title: string; meta_description: string; keywords: string; og_image: string }>>('/seo'),
  getBySlug: (slug: string) => apiRequest<{ page_slug: string; page_name: string; meta_title: string; meta_description: string; keywords: string; og_image: string }>(`/seo/${slug || 'homepage'}`),
  update: (pages: unknown) => apiRequest('/seo', { method: 'PUT', body: JSON.stringify(pages) }),
};

// =====================================================
// BLOG POSTS API (dedicated blog_posts table)
// =====================================================
export interface BlogPostData {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  author: string;
  status: 'published' | 'draft';
  read_time: string;
  tags: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_image: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export const blogApi = {
  list: (params?: { all?: boolean; category?: string; search?: string; page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.all) searchParams.set('all', 'true');
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    return apiRequest<BlogPostData[]>(`/blog?${searchParams}`);
  },

  getBySlug: (slug: string) => apiRequest<BlogPostData>(`/blog/${slug}`),

  getById: (id: number) => apiRequest<BlogPostData>(`/blog/id/${id}`),

  create: (data: Partial<BlogPostData>) =>
    apiRequest<BlogPostData>('/blog', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<BlogPostData>) =>
    apiRequest<BlogPostData>(`/blog/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    apiRequest(`/blog/${id}`, { method: 'DELETE' }),
};
